import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState, StatTile, Progress } from "@/components/ui";
import { HealthBadge, PriorityDot } from "@/components/badges";
import { fmtDate, priorityRank } from "@/lib/format";
import type { TaskItem } from "@shared/types";

export function MyTasks() {
  const { data, isLoading } = useQuery({
    queryKey: ["tasks", "page"],
    queryFn: () => api.get<{ tasks: TaskItem[]; summary: any }>("/tasks"),
  });
  const [showDone, setShowDone] = useState(false);

  const sorted = useMemo(() => {
    let list = data?.tasks ?? [];
    if (!showDone) list = list.filter((t) => !["done", "ahead", "cancelled"].includes(t.scheduleHealth));
    return [...list].sort((a, b) => {
      const rank = { delayed: 0, on_track: 1, planned: 2, done: 3, ahead: 3, cancelled: 4 };
      if (rank[a.scheduleHealth] !== rank[b.scheduleHealth]) return rank[a.scheduleHealth] - rank[b.scheduleHealth];
      if (a.endDate && b.endDate) return a.endDate.localeCompare(b.endDate);
      return priorityRank(a.priority) - priorityRank(b.priority);
    });
  }, [data, showDone]);

  if (isLoading) return <PageLoader />;
  const s = data!.summary;

  return (
    <>
      <PageHeader title="My Tasks" subtitle="Activities assigned to you across every project." />
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Assigned" value={s.total} />
        <StatTile label="Open" value={s.open} />
        <StatTile label="Due soon" value={s.dueSoon} accent={s.dueSoon ? "rgb(var(--accent-500))" : undefined} />
        <StatTile label="Delayed" value={s.delayed} accent={s.delayed ? "rgb(var(--st-delayed))" : undefined} />
      </div>

      <label className="mb-2 flex items-center gap-1.5 text-xs">
        <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Show completed / cancelled
      </label>

      {sorted.length === 0 ? (
        <EmptyState title="No open tasks" hint="When a project manager assigns you an activity, it shows up here." />
      ) : (
        <div className="card divide-y divide-line p-0">
          {sorted.map((t) => (
            <Link key={t.activityId} to={`/projects/${t.projectId}/schedule?activity=${t.activityId}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 hover:bg-surface-2/50">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{t.title}</p>
                <p className="text-xs text-ink-faint">{t.projectName}</p>
              </div>
              <PriorityDot value={t.priority} />
              <div className="flex w-32 items-center gap-2">
                <Progress value={t.percentComplete} className="flex-1" />
                <span className="text-xs tabular-nums">{Math.round(t.percentComplete)}%</span>
              </div>
              <span className="w-24 text-right text-xs text-ink-faint">{t.endDate ? fmtDate(t.endDate) : "no date"}</span>
              <HealthBadge health={t.scheduleHealth} />
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
